import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material'
import { CreditCard, Warning } from '@mui/icons-material'
import { Formik, getIn } from 'formik'
import { inject } from 'mobx-react'
// tslint:disable-next-line: no-var-requires
const CurrencyFormat: any = require('react-currency-format')
import Moment from 'moment'
import React from 'react'
import FullWidthField from 'src/common/FullWidthField'
import NumberMaskedInput from 'src/common/NumberMaskedInput'
import { IStores } from 'src/Stores'
import {
  createICheckFormValues,
  ICheckFormValues,
  toIApplyPayment,
} from 'src/providerRefunds/CheckFormValues'
import {
  getClaimPaymentDetails,
  retrieveRefundLineItemsWithBalance,
} from 'src/services/ClaimService'
import { VerifyCheck } from 'src/services/BillOutService'
import { IPaymentLineItem, PaymentLineItem } from 'src/viewModels/PaymentLIneItem'
import { formatDate } from 'src/utils/Formatter'
import {
  IPaymentLineItemDetailSummary,
  PaymentLineItemDetailSummary,
} from 'src/viewModels/PaymentLineItemDetailSummary'
import { ProcessReceivedCheckResults } from 'src/viewModels/ProcessReceivedCheckResult'
import { IPaymentDetailSummaryResult } from 'src/viewModels/PaymentDetailSummaryResult'
import { VerifyCheckRequest } from 'src/viewModels/VerifyCheckRequest'
import * as Yup from 'yup'
import moment from 'moment'
import ResponsiveAppBar from 'src/common/ResponsiveAppBar'
import { InboxActionSummary } from 'src/viewModels/workflow/InboxActionSummary'
import { ObjectiveProcessAction } from 'src/viewModels/workflow/ObjectiveProcessAction'
import { ILineItemSummaryResult } from 'src/viewModels/LineItemSummaryResult'
import ProcessReceivedCheckModal from 'src/common/ProcessReceivedCheckModal'
import { processAction, processDynamicAction } from 'src/services/WorkflowService'
import { OptionsObject } from 'notistack'
import { ClaimCustomForm } from 'src/utils/ClaimCustomForm'
import { BillStatusNumber } from 'src/utils/BillStatusNumber'

const maximumDate = moment().subtract(0, 'days')
const ProcessRefundModalSchema = Yup.object().shape({
  amount: Yup.number().required('Required').min(0, 'Please enter a positive number.'),
  checkNumber: Yup.string().required('Required'),
  datePaid: Yup.date()
    .max(maximumDate.toLocaleString(), 'Date Cannot be in the future!')
    .required('Required'),
  payer: Yup.string().required('Required'),
  applyAmount: Yup.number()
    .required('Required')
    .min(0, 'Please enter a positive number.'),
})

interface IProcessRefundModalProps {
  isOpen: boolean
  closeModal: () => void
  openProcessReceivedCheckDialog?: (results: ProcessReceivedCheckResults) => void
  openVerifyCheckDialog?: (results: ProcessReceivedCheckResults) => void
  action: InboxActionSummary
  isDynamicItem: boolean
  rerouteToPath?: (path: string) => void
  inboxItemId: number
  inboxId: string
  associatedItemId: string
  data: ObjectiveProcessAction
  entryUser: string
  nextInboxId?: number
  nextInboxText?: string
  handleProcessActionFinish?: (selectedAction?: InboxActionSummary) => void
  actionList: InboxActionSummary[]
  sendNotification?: (
    key: string,
    message: string,
    options?: OptionsObject,
    information?: string | undefined
  ) => void
}

class ProcessRefundModal extends React.Component<IProcessRefundModalProps> {
  public state = {
    isCheckVerified: false,
    isRefundValidated: false,
    totalMismatch: false,
    checkAmountLessThanRefundAmount: false,
    refundMoreThanBalance: false,
    lineItemDetailsWithBalance: undefined as unknown as IPaymentLineItem[],
    appliedPayments: undefined as unknown as IPaymentLineItemDetailSummary[],
    paymentSummary: undefined as unknown as IPaymentDetailSummaryResult,
    isLoading: false,
    comments: '',
    applyRefundWillInFull: false,
    payer: '',
    executed: false,
  }

  negativeArray = new Array<boolean>()

  public secondFormRef = React.createRef<any>()

  public async componentDidMount() {
    this.setState({
      isLoading: true,
    })

    this.validateActionConfiguration()

    const promises = [] as Array<Promise<void>>
    promises.push(this.loadLineItemDetailsWithBalance())
    promises.push(this.loadAppliedPayments())

    await Promise.all(promises).finally(() => {
      this.state.lineItemDetailsWithBalance?.forEach(() => {
        this.negativeArray.push(false)
      })

      this.setState({ isLoading: false })
    })
  }

  private validateActionConfiguration() {
    var paidInFull = this.props.actionList.find(
      (x) =>
        x.customFormName == ClaimCustomForm.ProcessRefund &&
        x.statusId == BillStatusNumber.Refunded &&
        x.id != this.props.action.id
    )
    if (paidInFull == null) {
      this.props.sendNotification!(
        '404',
        'This action is incorrectly configured. Please contact your administrator.',
        {
          variant: 'error',
        }
      )
      this.close()
    }
  }

  private loadLineItemDetailsWithBalance = async () => {
    await retrieveRefundLineItemsWithBalance(this.props.associatedItemId).then(
      (response: ILineItemSummaryResult[]) => {
        this.setState({
          lineItemDetailsWithBalance: response.map((item) => new PaymentLineItem(item)),
        })
      }
    )
  }

  private loadAppliedPayments = async () => {
    await getClaimPaymentDetails(this.props.associatedItemId).then(
      (response: IPaymentDetailSummaryResult) => {
        this.setState({
          appliedPayments: response.lineItems.map(
            (item) => new PaymentLineItemDetailSummary(item)
          ),
          paymentSummary: response,
          payer: response.insuranceCompanyName!,
        })
      }
    )
  }

  private getFormikValues = async () => {
    const formikValues = this.secondFormRef.current?.values
    await this.secondFormRef.current?.submitForm()
    return formikValues
  }

  private calculateTotalRefund(itemList: IPaymentLineItem[]): number {
    let totalRefund = 0
    for (const item of itemList) {
      const num = +(item.paymentAmount ?? 0)
      totalRefund += num * 100
    }
    return totalRefund / 100
  }

  private getTotalBalance(): number {
    return (
      this.state.lineItemDetailsWithBalance
        ?.map((x) => x.remainingBalance || 0)
        .reduce((a, b) => a! + b!, 0) ?? 0
    )
  }

  private saveCheck = async () => {
    const formValues = await this.getFormikValues()

    var i = 0
    for (const formValue of formValues.itemList) {
      const num = +(formValue.paymentAmount ?? 0)
      if (num < 0) {
        this.negativeArray[i] = true
        this.secondFormRef.current.setSubmitting(false)
        return
      } else {
        this.negativeArray[i] = false
      }
      i++
    }

    const totalRefund = this.calculateTotalRefund(formValues.itemList)
    const totalBalance = this.getTotalBalance()
    const totalMismatch = totalRefund != formValues.applyAmount
    const checkAmountLessThanRefundAmount =
      Number(formValues.amount ?? 0) < Number(formValues.applyAmount ?? 0)
    const refundMoreThanBalance = totalBalance < formValues.applyAmount
    const applyRefundWillInFull = totalBalance == Number(formValues.applyAmount ?? 0)

    this.setState(
      {
        totalMismatch,
        checkAmountLessThanRefundAmount,
        refundMoreThanBalance,
        applyRefundWillInFull,
      },
      () => {
        this.processValidation(formValues)
      }
    )
  }

  private processValidation = async (formValues: ICheckFormValues) => {
    if (this.state.totalMismatch || this.state.checkAmountLessThanRefundAmount) {
      this.setState({ isRefundValidated: false })
      this.secondFormRef.current.setSubmitting(false)
      return
    } else if (this.state.refundMoreThanBalance && !this.state.isRefundValidated) {
      this.setState({ isRefundValidated: true })
      this.secondFormRef.current.setSubmitting(false)
      return
    } else if (!this.state.isRefundValidated) {
      this.setState({ isRefundValidated: true })
      this.secondFormRef.current.setSubmitting(false)
    } else {
      this.setState({ isLoading: true, executed: true })

      try {
        this.props.data.processRefundData = toIApplyPayment(
          formValues,
          this.props.associatedItemId!
        )
        this.props.data.comment = this.state.comments

        let paidInFullAction = this.props.actionList.find(
          (x) =>
            x.customFormName == ClaimCustomForm.ProcessRefund &&
            x.statusId == BillStatusNumber.Refunded &&
            x.id != this.props.action.id
        )

        let executeAction =
          this.state.applyRefundWillInFull && paidInFullAction
            ? paidInFullAction
            : this.props.action

        if (this.props.isDynamicItem) {
          await processDynamicAction(
            this.props.data,
            this.props.inboxId.toString(),
            executeAction.id.toString(),
            this.props.associatedItemId.toString()
          )
          this.props.handleProcessActionFinish!(executeAction)
          this.props.closeModal()
        } else {
          await processAction(
            this.props.data,
            executeAction.id.toString(),
            this.props.inboxItemId.toString()
          )
          this.props.handleProcessActionFinish!(executeAction)
          this.close()
        }
      } catch (error) {
        this.setState({ executed: false, isLoading: false })
      }
    }
  }

  private close = () => {
    this.setState({
      isCheckVerified: false,
      isRefundValidated: false,
      totalMismatch: false,
      checkAmountLessThanRefundAmount: false,
      refundMoreThanBalance: false,
      applyRefundWillInFull: false,
      isLoading: false,
      executed: false,
      comments: '',
    })

    this.negativeArray = new Array<boolean>()
    this.props.closeModal!()
  }

  private renderCheckList() {
    return (
      <Table size="small">
        <TableHead>
          <TableRow className="gridPadding">
            <TableCell align="right">Check Date</TableCell>
            <TableCell>Check Number</TableCell>
            <TableCell>Payer</TableCell>
            <TableCell>Refund</TableCell>
            <TableCell align="right">Applied Date</TableCell>
            <TableCell>Applied By</TableCell>
            <TableCell align="right">DOS</TableCell>
            <TableCell align="right">Line#</TableCell>
            <TableCell>CPT Code</TableCell>
            <TableCell align="right">Amount Paid</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {this.state.appliedPayments ? (
            this.state.appliedPayments.map((record, index) => {
              return (
                <TableRow className="gridPadding" key={index}>
                  <TableCell align="right">{formatDate(record.checkDate!)}</TableCell>
                  <TableCell>{record.checkNumber}</TableCell>
                  <TableCell>{record.payer || ''}</TableCell>
                  <TableCell>{record.refund ? '✓' : ''}</TableCell>
                  <TableCell align="right">
                    {formatDate(record.appliedDatePaid!)}
                  </TableCell>
                  <TableCell>{record.appliedBy!}</TableCell>
                  <TableCell align="right">{formatDate(record.dateOfService!)}</TableCell>
                  <TableCell align="right">{record.lineNumber}</TableCell>
                  <TableCell>{record.cptCode}</TableCell>
                  <TableCell
                    align="right"
                    style={{
                      color:
                        record.amountPaid != undefined && Number(record.amountPaid) < 0
                          ? 'red'
                          : 'inherit',
                    }}
                  >
                    {record.amountPaid ? record.amountPaidDisplay : ''}
                  </TableCell>
                </TableRow>
              )
            })
          ) : (
            <TableRow>
              <TableCell colSpan={10} align="center">
                No payments found
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    )
  }

  private renderLineItemList(itemList: IPaymentLineItem[]) {
    return (
      <Table style={{ paddingTop: '10px' }} size="small">
        <TableHead>
          <TableRow key={'cpt-header'}>
            <TableCell align="right">Date of Service</TableCell>
            <TableCell align="right">Line</TableCell>
            <TableCell>CPT</TableCell>
            <TableCell colSpan={4}>Modifier</TableCell>
            <TableCell align="right">Contract Amount</TableCell>
            <TableCell align="right">Remaining Refund Balance</TableCell>
            <TableCell align="right">Amount</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {itemList ? (
            itemList!.map((y, lineIdx) => {
              return (
                <TableRow key={`q24-${lineIdx}`}>
                  <TableCell align="right">
                    {Moment(y.dateOfService).format('MM/DD/YYYY')}
                  </TableCell>
                  <TableCell align="right">{y.lineNumber}</TableCell>
                  <TableCell>{y.dProcedureCptHcpcs}</TableCell>
                  <TableCell>{y.dModifier1}</TableCell>
                  <TableCell>{y.dModifier2}</TableCell>
                  <TableCell>{y.dModifier3}</TableCell>
                  <TableCell>{y.dModifier4}</TableCell>
                  <TableCell align="right">
                    <CurrencyFormat
                      value={y.contractAmount}
                      displayType={'text'}
                      thousandSeparator={true}
                      prefix={'$'}
                      fixedDecimalScale={true}
                      decimalScale={2}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <CurrencyFormat
                      value={y.remainingBalance}
                      displayType={'text'}
                      thousandSeparator={true}
                      prefix={'$'}
                      fixedDecimalScale={true}
                      decimalScale={2}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <FullWidthField
                      variant="standard"
                      name={`itemList[${lineIdx}].paymentAmount`}
                      style={{ minWidth: '80px' }}
                      InputProps={{
                        inputComponent: NumberMaskedInput,
                        inputProps: {
                          currency: true,
                          guide: false,
                          numberOfDigits: 12,
                        },
                      }}
                      onKeyUp={() => this.resetPaymentValidate()}
                    />
                    {this.negativeArray[lineIdx] && (
                      <div
                        className="error"
                        style={{
                          fontSize: '10px',
                          position: 'absolute',
                        }}
                      >
                        Payment amount cannot be negative
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              )
            })
          ) : (
            <TableRow>
              <TableCell colSpan={10} align="center">
                No line items found
              </TableCell>
            </TableRow>
          )}
          <TableRow key="footer">
            <TableCell colSpan={7} align="right">
              <strong>Total:</strong>
            </TableCell>
            <TableCell align="right">
              {itemList ? (
                <CurrencyFormat
                  value={itemList!
                    .map((x) => x.contractAmount || 0)
                    .reduce((a, b) => a! + b!, 0)}
                  displayType={'text'}
                  thousandSeparator={true}
                  prefix={'$'}
                  fixedDecimalScale={true}
                  decimalScale={2}
                />
              ) : (
                <span>$0.00</span>
              )}
            </TableCell>
            <TableCell align="right">
              {itemList ? (
                <CurrencyFormat
                  value={itemList!
                    .map((x) => x.remainingBalance || 0)
                    .reduce((a, b) => a! + b!, 0)}
                  displayType={'text'}
                  thousandSeparator={true}
                  prefix={'$'}
                  fixedDecimalScale={true}
                  decimalScale={2}
                />
              ) : (
                <span>$0.00</span>
              )}
            </TableCell>
            <TableCell align="right">
              {itemList ? (
                <CurrencyFormat
                  value={itemList!
                    .map((x) => +(x.paymentAmount || 0))
                    .reduce((a, b) => a! + b!, 0)}
                  displayType={'text'}
                  thousandSeparator={true}
                  prefix={'$'}
                  fixedDecimalScale={true}
                  decimalScale={2}
                />
              ) : (
                <span>$0.00</span>
              )}
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    )
  }

  private resetVerify() {
    if (this.state.isCheckVerified != false) {
      this.setState({ isCheckVerified: false })
    }
    if (this.state.refundMoreThanBalance != false) {
      this.setState({ refundMoreThanBalance: false })
    }
    if (this.state.checkAmountLessThanRefundAmount != false) {
      this.setState({ checkAmountLessThanRefundAmount: false })
    }
  }

  private resetPaymentValidate() {
    if (this.state.isRefundValidated != false) {
      this.setState({ isRefundValidated: false })
    }
    if (this.state.refundMoreThanBalance != false) {
      this.setState({ refundMoreThanBalance: false })
    }
    if (this.state.checkAmountLessThanRefundAmount != false) {
      this.setState({ checkAmountLessThanRefundAmount: false })
    }
  }

  private verifyCheck = async (formValues: ICheckFormValues) => {
    this.setState({ isCheckVerified: true })
    var checkRequest = new VerifyCheckRequest()
    checkRequest.checkNumber = formValues.checkNumber || ''
    checkRequest.payer = formValues.payer || ''
    checkRequest.checkDate = formValues.datePaid || new Date()
    checkRequest.checkAmount = formValues.amount || 0
    checkRequest.refund = true

    await VerifyCheck(checkRequest).then((response: ProcessReceivedCheckResults) => {
      this.setState({ isCheckVerified: true })
      this.props.openVerifyCheckDialog!(response)
    })
  }

  public render() {
    const { isOpen } = this.props
    const hasValue = (field: string, values: any) => {
      const value = getIn(values, field)

      return !!value
    }
    return (
      <>
        <ProcessReceivedCheckModal />
        {this.state.isLoading && (
          <div
            style={{
              position: 'fixed',
              display: 'flex',
              width: '100%',
              height: '100%',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              cursor: 'pointer',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 2,
            }}
          >
            <CircularProgress size={100} />
          </div>
        )}
        {!this.state.isLoading && (
          <Dialog
            fullWidth={true}
            maxWidth="xl"
            open={isOpen!}
            onClose={(_, reason) => {
              if (reason !== 'backdropClick') {
                this.props.closeModal()
              }
            }}
          >
            <Formik
              initialValues={createICheckFormValues(
                this.props.associatedItemId!,
                undefined,
                this.state.lineItemDetailsWithBalance!
              )}
              onSubmit={() => {}}
              validationSchema={ProcessRefundModalSchema}
              innerRef={this.secondFormRef}
            >
              {({ values, isValid, errors }) => (
                <form id="secondForm">
                  <DialogTitle style={{ padding: 0 }}>
                    <ResponsiveAppBar
                      title="Process Refund"
                      pageIcon={<CreditCard />}
                      isModal={true}
                    />
                  </DialogTitle>
                  <DialogContent>
                    <Grid
                      container
                      direction="column"
                      spacing={2}
                      style={{ marginTop: 8 }}
                    >
                      <Grid container item direction="row" spacing={2}>
                        <Grid item style={{ whiteSpace: 'nowrap' }}>
                          <span style={{ fontWeight: 'bold', paddingRight: '10px' }}>
                            EOC/Legacy:
                          </span>
                          <span>{this.state.paymentSummary?.groupNumber}</span>
                        </Grid>
                        <Grid item style={{ whiteSpace: 'nowrap' }}>
                          <span style={{ fontWeight: 'bold', paddingRight: '10px' }}>
                            Patient Name:
                          </span>
                          <span>{this.state.paymentSummary?.patientName}</span>
                        </Grid>
                        <Grid item style={{ whiteSpace: 'nowrap' }}>
                          <span style={{ fontWeight: 'bold', paddingRight: '10px' }}>
                            Patient DOB:
                          </span>
                          <span>
                            {this.state.paymentSummary?.patientDob
                              ? moment(this.state.paymentSummary?.patientDob).format('L')
                              : ''}
                          </span>
                        </Grid>
                      </Grid>
                    </Grid>
                    <Grid
                      container
                      direction="column"
                      spacing={2}
                      style={{ marginTop: 4 }}
                    >
                      <Grid item container direction="row" spacing={2}>
                        <Grid item xs={12} md={2}>
                          <FullWidthField
                            variant="standard"
                            format="YYYY-MM-DD"
                            type="date"
                            name="datePaid"
                            label="Check Date"
                            inputId="datePaid"
                            fullWidth={true}
                            clearable={true}
                            shrink={true}
                            required
                            errorMessage={errors.datePaid}
                            onKeyUp={() => this.resetVerify()}
                          />
                        </Grid>
                        <Grid item xs={12} md={2}>
                          <FullWidthField
                            required
                            variant="standard"
                            name="checkNumber"
                            label="Check Number"
                            InputLabelProps={{
                              shrink: true,
                            }}
                            onKeyUp={() => this.resetVerify()}
                          />
                        </Grid>
                        <Grid item xs={12} md={3}>
                          <FullWidthField
                            required
                            variant="standard"
                            name="payer"
                            label="Payer"
                            InputLabelProps={{
                              shrink: true,
                            }}
                            onKeyUp={() => this.resetVerify()}
                          />
                        </Grid>
                        <Grid item xs={12} md={2}>
                          <FullWidthField
                            required
                            variant="standard"
                            name="amount"
                            label="Check Amount"
                            InputProps={{
                              inputComponent: NumberMaskedInput,
                              inputProps: {
                                currency: true,
                                guide: false,
                                numberOfDigits: 12,
                              },
                            }}
                            InputLabelProps={{
                              shrink: true,
                            }}
                            onKeyUp={() => this.resetVerify()}
                          />
                        </Grid>
                        <Grid item xs={12} md={1}>
                          <Button
                            onClick={() => this.verifyCheck(values as ICheckFormValues)}
                            size="medium"
                            variant="outlined"
                            className="gridButton"
                            color="primary"
                            data-cy="verify check"
                            disabled={
                              !hasValue('datePaid', values) ||
                              !hasValue('checkNumber', values) ||
                              !hasValue('amount', values) ||
                              !hasValue('payer', values) ||
                              this.state.isCheckVerified == true
                            }
                          >
                            {'Verify Check'}
                          </Button>
                        </Grid>
                        <Grid item xs={12} md={2} style={{ paddingLeft: '23px' }}>
                          <FullWidthField
                            required
                            variant="standard"
                            name="applyAmount"
                            label="Amount to Apply"
                            InputProps={{
                              inputComponent: NumberMaskedInput,
                              inputProps: {
                                currency: true,
                                guide: false,
                                numberOfDigits: 12,
                              },
                            }}
                            InputLabelProps={{
                              shrink: true,
                            }}
                            onKeyUp={() => this.resetPaymentValidate()}
                          />
                        </Grid>
                      </Grid>
                      <Grid item>
                        <Divider />
                      </Grid>
                      <Grid
                        item
                        container
                        direction="row"
                        wrap="nowrap"
                        justifyContent="center"
                        alignItems="center"
                      >
                        {this.renderLineItemList(values.itemList)}
                      </Grid>
                      <Accordion style={{ marginTop: '10px' }}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                          <Typography color="textSecondary" variant="body2">
                            PAST PROCESSED REFUNDS
                          </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                          <Grid
                            container
                            direction="row"
                            wrap="nowrap"
                            justifyContent="center"
                            alignItems="center"
                          >
                            {this.renderCheckList()}
                          </Grid>
                        </AccordionDetails>
                      </Accordion>
                      <Grid
                        item
                        container
                        direction="column"
                        wrap="nowrap"
                        alignItems="flex-end"
                      >
                        <Grid item xs={12} style={{ paddingTop: '0px' }}>
                          {this.state.totalMismatch && (
                            <div className="error">
                              Sum of claim line amounts must equal Amount to Apply
                              entered.
                            </div>
                          )}
                        </Grid>
                        <Grid item xs={12} style={{ paddingTop: '0px' }}>
                          {this.state.checkAmountLessThanRefundAmount && (
                            <div className="error">
                              Amount to Apply must be less than or equal to Check Amount.
                            </div>
                          )}
                        </Grid>
                        <Grid item xs={12} style={{ paddingTop: '0px' }}>
                          {this.state.refundMoreThanBalance &&
                            !this.state.checkAmountLessThanRefundAmount &&
                            !this.state.totalMismatch && (
                              <Grid item style={{ display: 'flex' }}>
                                <Warning
                                  alignmentBaseline="baseline"
                                  style={{
                                    color: '#ffa726',
                                    marginRight: '8px',
                                  }}
                                />
                                <Typography
                                  variant={'body1'}
                                  color="textPrimary"
                                  style={{ height: '18px' }}
                                >
                                  Amount to Apply is greater than total remaining balance.
                                </Typography>
                              </Grid>
                            )}
                        </Grid>
                      </Grid>
                      {this.props.action.displayComment && (
                        <Grid item xs={12} style={{ marginTop: '16px' }}>
                          <TextField
                            label="Comments"
                            name="notes"
                            variant="outlined"
                            InputLabelProps={{ shrink: true }}
                            fullWidth
                            required={this.props.action.requireComment}
                            multiline
                            minRows="4"
                            onChange={(event) =>
                              this.setState({ comments: event.target.value })
                            }
                          />
                        </Grid>
                      )}
                    </Grid>
                  </DialogContent>
                  <DialogActions>
                    <Button
                      variant="contained"
                      color="primary"
                      data-cy="save"
                      onClick={() => this.saveCheck()}
                      disabled={
                        !(
                          isValid &&
                          this.state.isCheckVerified &&
                          this.state.isRefundValidated &&
                          this.state.applyRefundWillInFull
                        )
                      }
                    >
                      Refunded In Full
                    </Button>
                    <Button
                      style={{
                        display: !this.state.isRefundValidated ? 'none' : undefined,
                      }}
                      variant="contained"
                      color="primary"
                      data-cy="save"
                      onClick={() => this.saveCheck()}
                      disabled={
                        !(
                          isValid &&
                          this.state.isRefundValidated &&
                          !this.state.applyRefundWillInFull
                        ) ||
                        (this.props.action.requireComment && this.state.comments === '')
                      }
                    >
                      Execute
                    </Button>
                    <Button
                      style={{
                        display: this.state.isRefundValidated ? 'none' : undefined,
                      }}
                      variant="contained"
                      color="primary"
                      data-cy="save"
                      onClick={() => this.saveCheck()}
                      disabled={
                        !(isValid && this.state.isCheckVerified) ||
                        (this.props.action.requireComment && this.state.comments === '')
                      }
                    >
                      Validate
                    </Button>
                    <Button onClick={this.close} data-cy="close">
                      Cancel
                    </Button>
                  </DialogActions>
                </form>
              )}
            </Formik>
          </Dialog>
        )}
      </>
    )
  }
}

const InjectedProcessRefundModal = inject<
  IStores,
  IProcessRefundModalProps,
  Partial<IProcessRefundModalProps>,
  any
>((stores: IStores) => ({
  openProcessReceivedCheckDialog: stores.receivePayments.openProcessReceivedCheckDialog,
  openVerifyCheckDialog: stores.receivePayments.openVerifyReceivedCheckDialog,
  sendNotification: stores.notifications.sendNotification,
}))(ProcessRefundModal)

export default InjectedProcessRefundModal
